import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Image from 'ls-common-client/dist/components/Image';
import Text from 'ls-common-client/dist/components/Text';
import Anchor from 'ls-common-client/dist/components/Anchor';
import Icon from 'ls-common-client/dist/components/Icon';
import EmptyButton from 'ls-common-client/dist/components/EmptyButton';
import TextReveal from 'ls-common-client/dist/components/TextReveal';
import LazyLoad from '../../components/LazyLoad';
import ImageLightbox from '../../components/Lightbox/ImageLightbox';
import { resize } from '../../services/imageResizer';
import { Context } from '../../context/AppContext';
import useDataLayerPush from '../../hooks/useDataLayerPush';
import { DATA_PUSH } from '../../components/Search/constants';

const StyledRoot = styled(Container)`
  border-bottom: solid 1px #ececec;
  margin-bottom: 20px;
  padding: 0 0 15px;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 10px;
  }
`;

const StyledImageContainer = styled(Container)`
  margin: 25px 0 10px;
`;

const StyledImageList = styled(Container)`
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
`;

const StyledImage = styled(Container)`
  overflow: hidden;
  border-radius: 11px;
  width: 106px;
  height: 106px;
  margin-right: 10px;
  margin-bottom: 10px;
  @media (max-width: 1024px) and (orientation: portrait), (max-width: 767px) {
    width: 80px;
    height: 80px;
  }
  @media (max-width: 767px) {
    width: 80px;
    height: 80px;
  }
`;

const StyledBusinessName = styled(Text)`
  display: block;
`;

const StyledAnchor = styled(Anchor)`
  &:hover {
    color: ${props => props.theme.primary.normal};

    & ${StyledBusinessName} {
      color: ${props => props.theme.primary.normal};
    }
  }
`;

const Enquiry = ({ enquiry }) => {
  const {
    media: { mobile },
    webp,
  } = useContext(Context);

  const {
    id,
    message,
    createdAt,
    enquiryAssets,
    enquiryResponse,
    profile,
    category,
  } = enquiry || {};
  const { displayName, logo, vanityUrlPath, status, contacts } = profile || {};
  const emailContact = contacts.find(contact => contact.type === 'email');
  const email = emailContact?.value;
  const { edges: assets = [] } = enquiryAssets || {};
  const { message: responseMessage } = enquiryResponse || {};
  const [photos, setPhotos] = useState([]);
  const { dataLayerPush } = useDataLayerPush();

  const pageDataLayerData = {
    event: DATA_PUSH.EVENT.ENQUIRIES_EMAIL_CLICK,
    cta_position_on_page: DATA_PUSH.CTA.ENQUIRIES_SECTION,
    business_name: displayName,
    profile_id: profile?.id,
    state: profile?.stateCode,
    suburb: profile?.suburb,
    package_id: profile?.package?.id || 'Free',
    package_type: profile?.package?.name || 'Free',
    business_category: category?.name,
  };

  useEffect(() => {
    setPhotos(assets);
  }, [assets]);

  if (status !== 'published') return null;
  return (
    <StyledRoot>
      <Container
        display="flex"
        justifyContent="space-between"
        marginBottom="15px"
      >
        <StyledAnchor href={`/profile/${vanityUrlPath}`} display="block">
          <Container display="flex">
            <Container>
              {logo && (
                <Container
                  src={resize(logo, {
                    width: 80,
                    height: 80,
                    webp,
                  })}
                  boxShadow="0 0 3px 0 rgba(0, 0, 0, 0.3)"
                  width="38px"
                  height="38px"
                  marginRight="10px"
                  borderRadius="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                >
                  <Image
                    objectFit="contain"
                    width="100%"
                    src={resize(logo, {
                      width: 80,
                      height: 80,
                      webp,
                    })}
                    alt="Business Logo"
                  />
                </Container>
              )}
            </Container>
            <Container>
              <Text
                fontSize="14px"
                color="text400"
                display="block"
                lineHeight="1.1"
                marginBottom="5px"
              >
                Your enquiry to
              </Text>
              <StyledBusinessName
                fontSize="18px"
                fontWeight="bold"
                lineHeight="1.1"
                color="normal"
                wordBreak="break-word"
              >
                {displayName}
              </StyledBusinessName>
            </Container>
          </Container>
        </StyledAnchor>

        <Container
          display="flex"
          alignItems="flex-end"
          justifyContent="flex-start"
          flexDirection="column"
          marginLeft="10px"
        >
          <Text
            fontSize={mobile ? '14px' : '16px'}
            color="text400"
            marginBottom="5px"
            whiteSpace="nowrap"
          >
            {moment(createdAt).format('DD MMM')}
          </Text>
        </Container>
      </Container>
      {email && (
        <Container
          display="flex"
          justifyContent="flex-start"
          marginBottom="15px"
        >
          <StyledAnchor
            href={`mailto:${email}`}
            onClick={() => {
              dataLayerPush(pageDataLayerData);
            }}
            borderRadius="50px"
            backgroundColor="background300"
            color="normal"
            fontSize="14px"
            padding="5px 13px"
            display="flex"
            alignItems="center"
            wordBreak="break-word"
            lineHeight="1.1"
          >
            <Icon
              marginBottom="-4px"
              marginRight="5px"
              iconSize="24px"
              className="ls-icon icon-categorypostoffices"
            />
            {email}
          </StyledAnchor>
        </Container>
      )}
      {message && (
        <Container marginBottom="15px">
          <TextReveal
            lines={3}
            wordBreak="break-word"
            display="block"
            fontSize="16px"
            renderShowMore={({ onClick, show }) => (
              <EmptyButton
                onClick={onClick}
                display="flex"
                alignItems="center"
                marginTop="10px"
                color="text300"
                fontSize="16px"
              >
                See {show ? 'Less' : 'More'}
                <Icon
                  iconSize="25px"
                  marginBottom="-7px"
                  className={`ls-icon icon-generalcaret${
                    show ? 'up' : 'down'
                  }medium`}
                />
              </EmptyButton>
            )}
          >
            {message}
          </TextReveal>
        </Container>
      )}
      {photos && !!photos.length && (
        <StyledImageContainer>
          <StyledImageList>
            <ImageLightbox
              name={`enquiryImage${id}`}
              images={photos.map(({ node: image }, key) => {
                return {
                  ...image,
                  children: (
                    <StyledImage key={key}>
                      <LazyLoad>
                        <img
                          src={resize(image, {
                            width: '106',
                            height: '106',
                            fit: 'cover',
                            webp,
                          })}
                          alt=""
                        />
                      </LazyLoad>
                    </StyledImage>
                  ),
                };
              })}
            />
          </StyledImageList>
        </StyledImageContainer>
      )}
      {responseMessage && (
        <Container
          backgroundColor="#eaefff"
          padding="20px"
          borderRadius="15px"
          fontSize="16px"
          marginTop="20px"
          marginBottom="15px"
        >
          <TextReveal
            lines={3}
            wordBreak="break-word"
            display="block"
            fontSize="16px"
            renderShowMore={({ onClick, show }) => (
              <EmptyButton
                onClick={onClick}
                display="flex"
                alignItems="center"
                marginTop="10px"
                color="text300"
                fontSize="16px"
              >
                See {show ? 'Less' : 'More'}
                <Icon
                  iconSize="25px"
                  marginBottom="-7px"
                  className={`ls-icon icon-generalcaret${
                    show ? 'up' : 'down'
                  }medium`}
                />
              </EmptyButton>
            )}
          >
            {responseMessage}
          </TextReveal>
        </Container>
      )}
    </StyledRoot>
  );
};

export default Enquiry;
